import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { PaginatedList } from 'app/shared/paginated-list';
import { PaginationParameters } from 'app/viewmodels/options/pagination-parameters';
import { ViewModel } from 'app/viewmodels/view-model';
import { Observable } from 'rxjs';
import { catchError, finalize, map, shareReplay } from 'rxjs/operators';
import { BackendServiceBase } from './backend-service-base';
import { CONFIG, Configuration } from './config';
import { ConfigService } from './config.service';
import { SpinnerService } from './spinner/spinner.service';

@Injectable({
  providedIn: 'root',
})
export class BackendPaginationService extends BackendServiceBase {
  private inFlightRequests: Map<string, Observable<any>> = new Map<string, Observable<any>>();
  constructor(
    private http: HttpClient,
    private spinner: SpinnerService,
    private configService: ConfigService,
  ) {
    super();

    const configuration: Configuration = this.configService.getConfiguration();

    if (configuration?.apiBaseUrl) {
      this.apiUrl = configuration.apiBaseUrl;
    }

    if (configuration?.authBaseUrl) {
      this.identityUrl = configuration.authBaseUrl;
    }
  }

  public hasResults<T extends ViewModel>(genericType: new () => T): Observable<boolean> {
    this.spinner.show();
    const route = new genericType().apiRoute;
    this.options.headers = this.getHeaders();
    this.options.params = new HttpParams();

    return this.http.get<boolean>(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.hasResults, this.options).pipe(
      map((hasResults: boolean) => hasResults),
      catchError(err => this.handleError('Could not determine if there are results', err)),
      finalize(() => this.spinner.hide()),
    );
  }

  public hasResultsWithParameters<T extends ViewModel>(
    genericType: new () => T,
    parameterObject: any,
    showSpinner: boolean = true,
  ): Observable<boolean> {
    if (showSpinner) {
      this.spinner.show();
    }

    const route = new genericType().apiRoute;
    this.options.headers = this.getHeaders();
    this.options.params = new HttpParams();

    return this.http
      .post<boolean>(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.hasResults, JSON.stringify(parameterObject), this.options)
      .pipe(
        map((hasResults: boolean) => hasResults),
        catchError(err => this.handleError('Could not determine if there are results', err)),
        finalize(() => {
          if (showSpinner) {
            this.spinner.hide();
          }
        }),
      );
  }

  public getCount<T extends ViewModel>(genericType: new () => T, showSpinner: boolean = true): Observable<number> {
    const route = new genericType().apiRoute;


    if (showSpinner) {
      this.spinner.show();
    }
    this.options.headers = this.getHeaders();
    this.options.params = new HttpParams();

    return this.http.get<number>(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.getCount, this.options).pipe(
      map((count: number) => count),
      catchError(err => this.handleError('Could not get number of results', err)),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    ) as Observable<number>;
  }

  public getCountWithParameters<T extends ViewModel>(
    genericType: new () => T,
    parameterObject: any,
    showSpinner: boolean = true,
    isIdentity: boolean = false,
  ): Observable<number> {
    const route = new genericType().apiRoute;
    const baseUrl = isIdentity ? this.identityUrl : this.apiUrl;

    this.options.headers = this.getHeaders();
    this.options.params = new HttpParams();

    const key = `${baseUrl}/${route}/${CONFIG.apiRoutes.pagination.getCount}|${JSON.stringify(
      parameterObject,
    )}`;

    if (this.inFlightRequests.has(key)) {
      return this.inFlightRequests.get(key) as Observable<number>;
    }

    if (showSpinner) {
      this.spinner.show();
    }

    const obs = this.http
      .post<number>(baseUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.getCount, JSON.stringify(parameterObject), this.options)
      .pipe(
        map((count: number) => count),
        catchError(err => this.handleError('Could not get number of results', err)),
        finalize(() => {
          this.inFlightRequests.delete(key);
          if (showSpinner) {
            this.spinner.hide();
          }
        }),
        shareReplay(1),
      ) as Observable<number>;

    this.inFlightRequests.set(key, obs);
    return obs;
  }

  public paginate<T extends ViewModel>(
    genericType: new () => T,
    pageNumber: number,
    pageSize: number,
    showSpinner: boolean = true,
    selectedItemId: string = null,
    parentId: string = null,
  ): Observable<T[]> {
    if (showSpinner) {
      this.spinner.show();
    }
    const route = new genericType().apiRoute;


    this.options.headers = this.getHeaders();

    if (selectedItemId === null && parentId === null) {
      this.options.params = this.createHttpParams({ pageNumber, pageSize });
    } else if (parentId === null) {
      this.options.params = this.createHttpParams({ pageNumber, pageSize, selectedItemId });
    } else {
      this.options.params = this.createHttpParams({ pageNumber, pageSize, selectedItemId, parentId });
    }

    return this.http.get<T[]>(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.paginate, this.options).pipe(
      map((results: T[]) =>
        results.map(r => {
          const item = new genericType();
          item.loadFromJson(r);
          return item;
        }),
      ),
      catchError(err => this.handleError('Could not get results', err)),
      finalize(() => {
        if (showSpinner) {
          this.spinner.hide();
        }
      }),
    ) as Observable<T[]>;
  }

  public paginateParameters<T extends ViewModel>(
    genericType: new () => T,
    parameters: PaginationParameters,
    showSpinner: boolean = true,
    apiRoute?: string
  ): Observable<T[]> {
    const route = apiRoute || new genericType().apiRoute;


    this.options.headers = this.getHeaders();

    return this.http
      .post<T[]>(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.paginate, JSON.stringify(parameters), this.options)
      .pipe(
        map((results: T[]) =>
          results.map(r => {
            const item = new genericType();
            item.loadFromJson(r);
            return item;
          }),
        ),
        catchError(err => this.handleError('Could not get results', err)),
        finalize(() => {
          if (showSpinner) {
            this.spinner.hide();
          }
        }),
      ) as Observable<T[]>;
  }

  public paginateParametersList<T extends ViewModel>(
    genericType: new () => T,
    parameters: PaginationParameters,
    showSpinner: boolean = true,
  ): Observable<PaginatedList<T>> {
    const route = new genericType().apiRoute;


    this.options.headers = this.getHeaders();

    return this.http
      .post<PaginatedList<T>>(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.paginate, JSON.stringify(parameters), this.options)
      .pipe(
        map((results: PaginatedList<T>) => {
          results.items = results.items.map(r => {
            const item = new genericType();
            item.loadFromJson(r);
            return item;
          });

          return results;
        }),
        catchError(err => this.handleError('Could not get results', err)),
        finalize(() => {
          if (showSpinner) {
            this.spinner.hide();
          }
        }),
      ) as Observable<PaginatedList<T>>;
  }

  public paginateWithParameters<T extends ViewModel>(
    genericType: new () => T,
    parameterObject: any,
    pageNumber: number,
    pageSize: number,
    showSpinner: boolean = true,
    isIdentity: boolean = false,
  ): Observable<T[]> {
    const route = new genericType().apiRoute;
    const baseUrl = isIdentity ? this.identityUrl : this.apiUrl;

    this.options.headers = this.getHeaders();
    this.options.params = this.createHttpParams({ pageNumber, pageSize });

    const key = `${baseUrl}/${route}/${CONFIG.apiRoutes.pagination.paginate}|${JSON.stringify(
      parameterObject,
    )}|${pageNumber}|${pageSize}`;

    if (this.inFlightRequests.has(key)) {
      return this.inFlightRequests.get(key) as Observable<T[]>;
    }

    if (showSpinner) {
      this.spinner.show();
    }

    const obs = this.http
      .post<T[]>(baseUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.paginate, JSON.stringify(parameterObject), this.options)
      .pipe(
        map((results: T[]) =>
          results.map(r => {
            const item = new genericType();
            item.loadFromJson(r);
            return item;
          }),
        ),
        catchError(err => this.handleError('Could not get results', err)),
        finalize(() => {
          this.inFlightRequests.delete(key);
          if (showSpinner) {
            this.spinner.hide();
          }
        }),
        shareReplay(1),
      ) as Observable<T[]>;

    this.inFlightRequests.set(key, obs);
    return obs;
  }

  public paginateSerializedData(
    parameterObject: any,
    route: string,
    pageNumber: number,
    pageSize: number,
    showSpinner: boolean = true,
  ): Observable<any> {
    if (showSpinner) {
      this.spinner.show();
    }


    this.options.headers = this.getHeaders();
    this.options.params = this.createHttpParams({ pageNumber, pageSize });

    return this.http
      .post(this.apiUrl + '/' + route + '/' + CONFIG.apiRoutes.pagination.paginate, JSON.stringify(parameterObject), this.options)
      .pipe(
        map((result: any) => JSON.parse(result)),
        catchError(err => this.handleError('Could not get results', err)),
        finalize(() => {
          if (showSpinner) {
            this.spinner.hide();
          }
        }),
      );
  }
}
